/**
 * QR Code client-side utilities.
 *
 * NOTE: QR code *generation* has moved to src/api/qrApi.js.
 * This file retains only the download helper.
 */
import axios from 'axios';

/**
 * Triggers a file-system download of the generated QR code image.
 *
 * Handles three URL types:
 *  - blob:   — object URLs created by qrApi (most common)
 *  - data:   — base64 data URIs (legacy / fallback)
 *  - https:  — remote URLs that need to be fetched first
 *
 * @param {string} qrImageUrl - The image source (blob URL, data URI, or remote URL)
 * @param {string} [filename='qr-code.png']
 * @returns {Promise<void>}
 * @throws {Error} if the fetch fails for remote URLs
 */
export const downloadQRCode = async (qrImageUrl, filename = 'qr-code.png') => {
  // Blob URLs and data URIs can be used directly as <a href>
  if (qrImageUrl.startsWith('blob:') || qrImageUrl.startsWith('data:')) {
    const link = document.createElement('a');
    link.href = qrImageUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    return;
  }

  // Remote URL — fetch as blob first so the browser treats it as a download
  // rather than navigating to the image
  const response = await axios.get(qrImageUrl, { responseType: 'blob' });
  const blobUrl = window.URL.createObjectURL(response.data);

  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  // Clean up the temporary object URL
  window.URL.revokeObjectURL(blobUrl);
};
